/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { getConfig } from './config.js';
import {
    currentHost,
    getRelativeLink,
    isExternalLink,
    rewriteInternalUrlsForApp,
    urlExists,
} from './links.js';
import { languageDisplayName } from './util.js';

/*
 * Media URLs and players.
 *
 * A map's media is addressed differently depending on where the map is
 * being served from, so a URL stored in content has to be rewritten before
 * it is requested. The client and map identifiers for that come from the
 * configuration payload the backend injects.
 *
 * The rest builds real players out of links and elements: subtitle tracks,
 * `<video>`/`<audio>` replacements, and pausing whatever is playing.
 */

/**
 * Checks if a given string is a media URL.
 *
 * This function determines if the provided URL references a media asset.
 *
 * @function
 * @param {string} url - The URL to be checked.
 * @returns {boolean} Returns true if the URL is identified as a media URL, otherwise false.
 */
export const isMediaURL = (url) => {
    return /\/media\//.test(url);
};

/**
 * Parses a media URL of a map and returns the parsed parts as an object.
 *
 * Three URL formats are recognized:
 *  - API URL: /api/v1/media/{clientId}/{mapId}/{variant}/{filename}
 *  - Legacy URL: /client/{clientId}/{mapId}/media/{variant}/{filename}
 *  - Client domain (CNAME): /{mapId}/media/{variant}/{filename}
 *
 * Returns an empty object when the URL is external or does not match any
 * of the known patterns.
 *
 * @example
 * // API URL → {clientId: "foobar", mapId: "42", variant: "default", filename: "video.mov", variantSuffix: ".mp4"}
 * parseMediaURL("/api/v1/media/foobar/42/default/video.mov.mp4");
 *
 * @example
 * // Legacy URL → {clientId: "foobar", mapId: "42", variant: "small", filename: "image.jpg", variantSuffix: ".jpg"}
 * parseMediaURL("/client/foobar/42/media/small/image.jpg.jpg");
 *
 * @example
 * // Client domain (CNAME) → {clientId: "my-client", mapId: "my-map", variant: "default", filename: "photo.JPG", variantSuffix: ".jpg"}
 * parseMediaURL("https://www.example.com/my-map/media/default/photo.JPG.jpg");
 *
 * @param href - The URL string.
 * @returns - The object containing attributes for the parsed parts of the URL.
 * @type {(href: string) => {clientId : string, mapId : string, variant : string?, filename : string, variantSuffix : string?} | Object}
 */
export const parseMediaURL = (href) => {
    const parsed = {
        clientId: undefined,
        mapId: undefined,
        variant: undefined,
        filename: Number.NaN,
        variantSuffix: undefined
    };
    if (isExternalLink(href) || !isMediaURL(href)) {
        return parsed;
    }
    const relativeLink = getRelativeLink(href);

    const legacyUrlMatch =
        /^\/client\/([^/]+)\/([^/]+)\/media\/(([^/]+)\/)?(.+?)(\.\w+)?$/.exec(
            relativeLink
        );
    const apiUrlMatch =
        /^\/api\/v1\/media\/([^/]+)\/([^/]+)\/(([^/]+)\/)?(.+?)(\.\w+)?$/.exec(
            relativeLink
        );
    const urlMatch = legacyUrlMatch || apiUrlMatch;

    if (urlMatch) {
        const [, clientId, mapId, , variant, filenameBase, suffix] = urlMatch;
        const filename = variant ? filenameBase : filenameBase + suffix;
        const variantSuffix = variant ? suffix : undefined;
        return { clientId, mapId, variant, filename, variantSuffix };
    }

    // Client domain (CNAME): /{mapId}/media/{variant}/{filename}
    if (currentHost().isClientDomain) {
        const clientDomainMatch =
            /^\/([^/]+)\/media\/(([^/]+)\/)?(.+?)(\.\w+)?$/.exec(
                relativeLink
            );
        if (clientDomainMatch) {
            const [, mapId, , variant, filenameBase, suffix] =
                clientDomainMatch;
            const clientId = getConfig()?.client?.id || '';
            const filename = variant
                ? filenameBase
                : filenameBase + (suffix || '');
            const variantSuffix = variant ? suffix : undefined;
            return { clientId, mapId, variant, filename, variantSuffix };
        }
    }

    return parsed;
};

/**
 * Rewrite a media URL of a map.
 *
 * Output format depends on context:
 *  - Client domain (CNAME): /{mapId}/media/{variant}/{filename}{suffix}
 *  - useLegacy: /client/{clientId}/{mapId}/media/{variant}/{filename}{suffix}
 *  - default: /api/v1/media/{clientId}/{mapId}/{variant}/{filename}{suffix}
 *
 * Returns href unchanged when it cannot be parsed as a media URL.
 *
 * @example
 * // API URL, change variant and format
 * // → "/api/v1/media/foobar/42/small/video.mov.webm"
 * rewriteMediaURL("/api/v1/media/foobar/42/default/video.mov.mp4", { variant: 'small', variantSuffix: '.webm' });
 *
 * @example
 * // Legacy URL (useLegacy: true), keep variant from source URL
 * // → "/client/foobar/42/media/default/image.jpg.jpg"
 * rewriteMediaURL("/api/v1/media/foobar/42/default/image.jpg.jpg", { useLegacy: true });
 *
 * @example
 * // Client domain (CNAME), change to small AVIF variant
 * // → "/my-map/media/small/photo.JPG.avif"
 * rewriteMediaURL("https://www.example.com/my-map/media/default/photo.JPG.jpg", { variant: 'small', variantSuffix: '.avif' });
 *
 * @example
 * // Non-media URL — returned unchanged
 * rewriteMediaURL("https://external.example.com/image.jpg");
 *
 * @param href - A URL string.
 * @param options - An object with option properties to change different parts of the generated URL.
 * @returns - The generated URL string.
 * @type {(href : string, options : {defaultVariant : string, useLegacy : boolean, variant : string, variantSuffix : string}) => string}
 */
export const rewriteMediaURL = (href, options = {}) => {
    if (isExternalLink(href) || !isMediaURL(href)) {
        return href;
    }
    const {
        clientId: urlClientId,
        mapId: urlMapId,
        variant,
        filename,
        variantSuffix
    } = parseMediaURL(href);
    // Legacy /client/{slug}/... URLs use client.slug; API /api/v1/media/{id}/... uses client.id.
    const clientSlug = getConfig()?.client?.slug || urlClientId;
    const clientId = getConfig()?.client?.id || urlClientId;
    const mapId = getConfig()?.map?.id || urlMapId;
    const mapSlug = getConfig()?.map?.slug;

    const mediaOptions = { ...options };

    if (mediaOptions.defaultVariant === undefined) {
        mediaOptions.defaultVariant = 'default';
    }

    let variantStr =
        mediaOptions.variant || variant || mediaOptions.defaultVariant || '';
    if (variantStr) {
        variantStr += '/';
    }

    const variantSuffixStr = variantStr
        ? mediaOptions.variantSuffix || variantSuffix || ''
        : '';

    const mapIdOrSlug =
        getConfig()?.map?.rewrite === 'id' ? mapId : mapSlug;
    if (currentHost().isClientDomain) {
        // On CNAME client domains the mapId is already the map slug taken
        // from the URL path, so we can construct the URL directly without
        // going through rewriteInternalUrlsForApp
        return `/${mapIdOrSlug}/media/${variantStr}${filename}${variantSuffixStr}`;
    } else if (mediaOptions.useLegacy) {
        return `/client/${clientSlug}/${mapIdOrSlug}/media/${variantStr}${filename}${variantSuffixStr}`;
    } else {
        return `/api/v1/media/${clientId}/${mapId}/${variantStr}${filename}${variantSuffixStr}`;
    }
};

/**
 * Rewrite the URL of an image asset.
 *
 * Determines the correct variantSuffix from the image file extension
 * (jpg/jpeg → .jpg, png → .png, webp → .webp, avif → .avif) and
 * delegates to rewriteMediaURL(). URLs with unrecognized extensions
 * (e.g. .gif, .svg) are passed through rewriteInternalUrlsForApp()
 * unchanged.
 *
 * @example
 * // API URL, default variant → "/api/v1/media/foobar/42/default/photo.jpg.jpg"
 * rewriteImageURL("/api/v1/media/foobar/42/default/photo.jpg");
 *
 * @example
 * // API URL, explicit small variant → "/api/v1/media/foobar/42/small/photo.jpg.webp"
 * rewriteImageURL("/api/v1/media/foobar/42/default/photo.jpg", { variant: 'small', variantSuffix: '.webp' });
 *
 * @example
 * // Client domain (CNAME) → "/my-map/media/default/photo.JPG.jpg"
 * rewriteImageURL("https://www.example.com/my-map/media/default/photo.JPG.jpg");
 *
 * @example
 * // Unrecognised extension (.gif) — passed through unchanged
 * rewriteImageURL("/client/foobar/42/media/default/anim.gif");
 *
 * @param imageUrl - A URL
 * @param options - Options that will be passed to rewriteMediaURL()
 * @returns - A rewritten URL
 * @type {(imageUrl: string, options: Object) => string}
 */
export const rewriteImageURL = (imageUrl, options = {}) => {
    if (!imageUrl) {
        return imageUrl;
    }
    const imageSuffix = imageUrl.split('.').pop().toLowerCase();
    const variantSuffix = {
        avif: '.avif',
        jpeg: '.jpg',
        jpg: '.jpg',
        png: '.png',
        webp: '.webp'
    }[imageSuffix];
    if (!variantSuffix) {
        return rewriteInternalUrlsForApp(imageUrl);
    }
    const imageOptions = { ...options, variantSuffix };
    return rewriteMediaURL(imageUrl, imageOptions);
};

/**
 * Rewrite the URLs of all media elements in an HTML container element.
 * @param container - DOM container element
 * @param options - An object with option properties to change different parts of the generated URL
 * @type {(container: Object, options: Object) => *}
 */
export const rewriteMediaUrls = (container, options) => {

    const mediaOptions = { ...options };

    // Rewrite URLs of image elements
    container.querySelectorAll('img').forEach((image) => {
        if (image.src && !isExternalLink(image.src)) {
            image.src = rewriteImageURL(image.src, mediaOptions);
        }
    });

    // Rewrite URLs of audio and video elements
    container.querySelectorAll('audio, video').forEach((avMedia) => {
        if (avMedia.src && !isExternalLink(avMedia.src)) {
            avMedia.src = rewriteMediaURL(avMedia.src, mediaOptions);
        }
        if (avMedia.poster && !isExternalLink(avMedia.poster)) {
            avMedia.poster = rewriteImageURL(
                avMedia.poster,
                mediaOptions
            );
        }
        avMedia.querySelectorAll('source').forEach((mediaSource) => {
            if (
                mediaSource.src &&
                !isExternalLink(mediaSource.src)
            ) {
                mediaSource.src = rewriteMediaURL(
                    mediaSource.src,
                    mediaOptions
                );
            }
        });
    });

    // Rewrite links to media assets
    const mediaLinkOptions = { ...mediaOptions, defaultVariant: '' };
    container.querySelectorAll('a[href]').forEach((link) => {
        if (
            link.href &&
            !isExternalLink(link.href) &&
            isMediaURL(link.href)
        ) {
            link.href = rewriteMediaURL(link.href, mediaLinkOptions);
        }
    });
};

export const rewriteImageElement = (elem, options = {}) => {
    const imageElem = elem.tagName === 'IMG' ? elem : elem.querySelector('img');
    const url = imageElem?.src;

    if (!url || isExternalLink(url)) return;

    const mediaOptions = { ...options };

    const [mediaUrl, urlFragments] = url.split('#');
    const imageUrl = rewriteImageURL(mediaUrl, mediaOptions);
    const mediaFragments = urlFragments ? `#${urlFragments}` : '';

    imageElem.src = `${imageUrl}${mediaFragments}`;
};

export const rewriteVideoElement = (elem, options = {}) => {
    const videoSourceElem = elem.hasAttribute('src')
        ? elem
        : elem.querySelector('source');
    const url = videoSourceElem?.src;

    if (!url || isExternalLink(url)) return;

    const mediaOptions = { ...options };

    const [mediaUrl, urlFragments] = url.split('#');
    const baseUrl = rewriteMediaURL(
        mediaUrl.replace(/(\.[a-z0-9]{3,5})\.(mp4|webm)$/i, '$1'),
        mediaOptions
    );
    const mediaFragments = urlFragments ? `#${urlFragments}` : '';

    ['src', 'width', 'height'].forEach((attr) => elem.removeAttribute(attr));
    elem.preload = 'none';
    elem.setAttribute('controls', 'controls');

    elem.querySelectorAll('source, track').forEach((sourceOrTrack) =>
        sourceOrTrack.parentElement.removeChild(sourceOrTrack)
    );

    const webmSource = document.createElement('source');
    webmSource.src = `${baseUrl}.webm${mediaFragments}`;
    webmSource.type = 'video/webm; codecs="vp9, opus"';
    elem.appendChild(webmSource);

    const mp4Source = document.createElement('source');
    mp4Source.src = `${baseUrl}.mp4${mediaFragments}`;
    mp4Source.type = 'video/mp4; codecs="avc1.4D401F, mp4a.40.2"';
    elem.appendChild(mp4Source);

    let posterUrl = `${baseUrl.replace(
        /\.(mp4|webm)$/,
        '.jpg'
    )}.jpg${mediaFragments}`;
    urlExists(posterUrl).then((exists) => {
        if (!exists) posterUrl = `${baseUrl}.jpg${mediaFragments}`;
        elem.poster = posterUrl;
    });

    attachSubtitleTrack(elem, baseUrl, mediaFragments);
};

export const rewriteAudioElement = (elem, options = {}) => {
    const audioSourceElem = elem.hasAttribute('src')
        ? elem
        : elem.querySelector('source');
    const url = audioSourceElem?.src;

    if (!url || isExternalLink(url)) return;

    const mediaOptions = { ...options };

    const [mediaUrl, urlFragments] = url.split('#');
    const baseUrl = rewriteMediaURL(
        mediaUrl.replace(/(\.[a-z0-9]{3,5})\.(mp3|opus)$/i, '$1'),
        mediaOptions
    );
    const mediaFragments = urlFragments ? `#${urlFragments}` : '';

    ['src'].forEach((attr) => elem.removeAttribute(attr));
    elem.preload = 'none';
    elem.setAttribute('controls', 'controls');

    elem.querySelectorAll('source, track').forEach((sourceOrTrack) =>
        sourceOrTrack.parentElement.removeChild(sourceOrTrack)
    );

    const opusSource = document.createElement('source');
    opusSource.src = `${baseUrl}.opus${mediaFragments}`;
    opusSource.type = 'audio/opus';
    elem.appendChild(opusSource);

    const mp3Source = document.createElement('source');
    mp3Source.src = `${baseUrl}.mp3${mediaFragments}`;
    mp3Source.type = 'audio/mp3';
    elem.appendChild(mp3Source);
};

export const rewriteMediaElements = (container, options = {}) => {
    container
        .querySelectorAll('img, picture')
        .forEach((elem) => rewriteImageElement(elem, options));
    container
        .querySelectorAll('video')
        .forEach((elem) => rewriteVideoElement(elem, options));
    container
        .querySelectorAll('audio')
        .forEach((elem) => rewriteAudioElement(elem, options));
};

/**
 * Stop all playing media elements in a HTML container element.
 * @param container - DOM container element
 * @type {(container: Object) => undefined}
 */
export const stopAudioVideo = (container) => {
    const root = container;
    root.querySelectorAll('audio, video').forEach((avMedia) => {
        if (!avMedia.paused) {
            avMedia.pause();
        }
    });
};

/**
 * Attach a subtitle track to a media element, if one has been published.
 *
 * Probes the map's own language before falling back to German, which is
 * what all three call sites used to hardcode -- so a French map with French
 * subtitles beside it offered none, while a German file was attached to
 * every language.
 *
 * Does nothing when the author already supplied a track: theirs is better
 * than anything guessed from a filename.
 *
 * @param {HTMLMediaElement} mediaElem - Element to attach the track to.
 * @param {string} baseUrl - Media URL without its extension.
 * @param {string} [mediaFragments] - Trailing `#t=...` fragment, if any.
 * @returns {void}
 */
export const attachSubtitleTrack = (mediaElem, baseUrl, mediaFragments) => {
    if (mediaElem.querySelector('track')) return;

    const fragments = mediaFragments || '';
    const mapLang = (getConfig()?.i18n?.lang || '').split('_')[0];
    const candidates = [];
    if (mapLang) candidates.push(mapLang);
    if (!candidates.includes('de')) candidates.push('de');

    const tryNext = (index) => {
        if (index >= candidates.length) return;
        const lang = candidates[index];
        const subtitleUrl = `${baseUrl}.${lang}.vtt${fragments}`;
        urlExists(subtitleUrl).then((exists) => {
            if (!exists) {
                tryNext(index + 1);
                return;
            }
            const track = document.createElement('track');
            track.setAttribute('label', languageDisplayName(lang));
            track.setAttribute('kind', 'subtitles');
            track.setAttribute('srclang', lang);
            track.setAttribute('default', 'default');
            track.src = subtitleUrl;
            mediaElem.appendChild(track);
        });
    };
    tryNext(0);
};

/**
 * Replaces `<video>` elements inside `elem` with fully-configured players
 * that include WebM and MP4 sources, a poster image, and optional subtitle tracks.
 * @param {Element} elem - Root element to search within.
 * @param {object} [rewriteOptions] - Passed to {@link rewriteMediaURL}.
 * @returns {void}
 */
export const videoToVideoPlayer = (elem, rewriteOptions) => {
    const root = elem;
    root.querySelectorAll('video').forEach((videoElem) => {
        const srcAttr = videoElem.getAttribute('src');
        const firstSource = videoElem.querySelector('source');
        const url =
            srcAttr || (firstSource ? firstSource.getAttribute('src') : '');
        if (!url || isExternalLink(url)) return;

        const [mediaUrl, urlFragments] = url.split('#');
        const baseUrl = rewriteMediaURL(
            mediaUrl.replace(/(\.[a-z0-9]{3,5})\.(mp4|webm)$/i, '$1'),
            rewriteOptions
        );
        const mediaFragments = urlFragments ? `#${urlFragments}` : '';

        // reset attributes
        videoElem.removeAttribute('src');
        videoElem.removeAttribute('width');
        videoElem.removeAttribute('height');
        videoElem.setAttribute('controls', 'controls');
        videoElem.setAttribute('preload', 'none');

        // `track` deliberately excluded: removing it discarded captions the
        // author had published alongside the video.
        videoElem
            .querySelectorAll('source')
            .forEach((n) => n.parentElement.removeChild(n));

        const sWebm = document.createElement('source');
        sWebm.src = `${baseUrl}.webm${mediaFragments}`;
        sWebm.type = 'video/webm; codecs="vp9, opus"';
        videoElem.appendChild(sWebm);

        const sMp4 = document.createElement('source');
        sMp4.src = `${baseUrl}.mp4${mediaFragments}`;
        sMp4.type = 'video/mp4; codecs="avc1.4D401F, mp4a.40.2"';
        videoElem.appendChild(sMp4);

        let posterUrl = `${baseUrl.replace(/\.(mp4|webm)$/, '.jpg')}.jpg${mediaFragments}`;
        urlExists(posterUrl).then((exists) => {
            if (!exists) posterUrl = `${baseUrl}.jpg${mediaFragments}`;
            videoElem.setAttribute('poster', posterUrl);
        });

        attachSubtitleTrack(videoElem, baseUrl, mediaFragments);
    });
};

/**
 * Replaces `<a href="*.mp4">` / `<a href="*.webm">` links inside `elem` with
 * inline `<video>` players.
 * @param {Element} elem - Root element to search within.
 * @param {object} [rewriteOptions] - Passed to {@link rewriteMediaURL}.
 * @returns {void}
 */
export const linksToVideoPlayer = (elem, rewriteOptions) => {
    const root = elem;
    root.querySelectorAll('a[href]').forEach((link) => {
        const url = link.getAttribute('href');
        if (isExternalLink(url) || !/\.(mp4|webm)($|#)/.test(url))
            return;

        const [mediaUrl, urlFragments] = url.split('#');
        const baseUrl = rewriteMediaURL(
            mediaUrl.replace(/\.(mp4|webm)$/, ''),
            rewriteOptions
        );
        const mediaFragments = urlFragments ? `#${urlFragments}` : '';

        const videoElem = document.createElement('video');
        videoElem.setAttribute('controls', 'controls');
        videoElem.setAttribute('loading', 'lazy');
        videoElem.setAttribute('preload', 'none');

        const sWebm = document.createElement('source');
        sWebm.src = `${baseUrl}.webm${mediaFragments}`;
        sWebm.type = 'video/webm; codecs="vp9, opus"';
        videoElem.appendChild(sWebm);

        const sMp4 = document.createElement('source');
        sMp4.src = `${baseUrl}.mp4${mediaFragments}`;
        sMp4.type = 'video/mp4; codecs="avc1.4D401F, mp4a.40.2"';
        videoElem.appendChild(sMp4);

        let posterUrl = `${baseUrl.replace(/\.(mp4|webm)$/, '.jpg')}.jpg${mediaFragments}`;
        urlExists(posterUrl).then((exists) => {
            if (!exists) posterUrl = `${baseUrl}.jpg${mediaFragments}`;
            videoElem.setAttribute('poster', posterUrl);
        });

        attachSubtitleTrack(videoElem, baseUrl, mediaFragments);

        link.replaceWith(videoElem);
    });
};

/**
 * Replaces `<a href="*.mp3">` / `<a href="*.opus">` links inside `elem` with
 * inline `<audio>` players.
 * @param {Element} elem - Root element to search within.
 * @param {object} [rewriteOptions] - Passed to {@link rewriteMediaURL}.
 * @returns {void}
 */
export const linksToAudioPlayer = (elem, rewriteOptions) => {
    const root = elem;
    root.querySelectorAll('a[href]').forEach((link) => {
        const url = link.getAttribute('href');
        if (isExternalLink(url) || !/\.(mp3|opus)($|#)/.test(url))
            return;

        const [mediaUrl, urlFragments] = url.split('#');
        const baseUrl = rewriteMediaURL(
            mediaUrl.replace(/\.(mp3|opus)$/, ''),
            rewriteOptions
        );
        const mediaFragments = urlFragments ? `#${urlFragments}` : '';

        const audioElem = document.createElement('audio');
        audioElem.setAttribute('controls', 'controls');
        audioElem.setAttribute('loading', 'lazy');
        audioElem.setAttribute('preload', 'none');

        const sOpus = document.createElement('source');
        sOpus.src = `${baseUrl}.opus${mediaFragments}`;
        sOpus.type = 'audio/opus';
        audioElem.appendChild(sOpus);

        const sMp3 = document.createElement('source');
        sMp3.src = `${baseUrl}.mp3${mediaFragments}`;
        sMp3.type = 'audio/mp3';
        audioElem.appendChild(sMp3);

        link.replaceWith(audioElem);
    });
};

/**
 * Convenience wrapper: calls {@link videoToVideoPlayer},
 * {@link linksToVideoPlayer}, and {@link linksToAudioPlayer}.
 * @param {Element} elem - Root element to search within.
 * @param {object} [rewriteOptions] - Passed to each underlying function.
 * @returns {void}
 */
export const linksToMediaPlayer = (elem, rewriteOptions) => {
    videoToVideoPlayer(elem, rewriteOptions);
    linksToVideoPlayer(elem, rewriteOptions);
    linksToAudioPlayer(elem, rewriteOptions);
};
